import { useState } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Link,
    Chip,
    Tooltip,
    IconButton,
    Menu,
    MenuItem
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { FileStatusIndicator } from './FileStatusIndicator';
import ViewArchivedPlanInfo from './ViewArchivedPlanInfo';
import { WithPermission } from '../../Auth/WithPermission';

export const ArchivedPlansBuilders = ({ builders, expandedProjects, onProjectToggle, expandedBuilders, onBuilderToggle, searchTerm = '', handleRestorePlan }) => {
    
    const [viewingPlan, setViewingPlan] = useState(null);

    const handleExpandBuilder = (builderId) => (event, isExpanded) => {
        onBuilderToggle(builderId, isExpanded);
    };

    const handleExpandProject = (projectId) => (event, isExpanded) => {
        onProjectToggle(projectId, isExpanded);
    };

    // Group plans by project within each builder
    const groupedBuilders = builders.map(builder => {
        const projectsMap = builder.plans.reduce((acc, plan) => {
            if (!acc[plan.project]) {
                acc[plan.project] = {
                    id: `${builder.id}-${plan.project}`,
                    name: plan.project,
                    plans: [],
                    archived: plan.projectArchived
                };
            }
            
            // Only add non-empty plans to the plans array
            if (!plan.id.startsWith('empty-project-') && (plan.planNumber || plan.elevation || plan.fileName)) {
                acc[plan.project].plans.push(plan);
            }
            
            return acc;
        }, {});

        return {
            ...builder,
            projects: Object.values(projectsMap)
        };
    }).filter(builder => {
        // Only keep builders that have at least one project with at least one plan
        return builder.projects.some(project => project.plans.length > 0);
    });

    const [expandedRows, setExpandedRows] = useState(new Set());

    const formatDateTooltip = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDateTable = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const ITEM_HEIGHT = 48;
    const [anchorEl, setAnchorEl] = useState(null);
    const [activeMenuRow, setActiveMenuRow] = useState(null); // Track which row's menu is open
    const handleMenuClick = (event, rowId) => {
        // Open menu for this row
        setAnchorEl(event.currentTarget);
        setActiveMenuRow(rowId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setActiveMenuRow(null);
    };

    // Helper function
    function escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    const HighlightText = ({ text, searchTerm }) => {
        if (!searchTerm.trim() || !text) return text;

        const parts = String(text).split(new RegExp(`(${escapeRegExp(searchTerm)})`, 'gi'));
        return (
            <span>
                {parts.map((part, i) =>
                    part.toLowerCase() === searchTerm.toLowerCase() ?
                        <strong key={i}>{part}</strong> :
                        part
                )}
            </span>
        );
    };

    return (
        <Box sx={{ width: '100%' }}>

            {groupedBuilders.map((builder) => (
                <Accordion
                    key={builder.id}
                    expanded={expandedBuilders[builder.id] || searchTerm || false}
                    onChange={handleExpandBuilder(builder.id)}
                    sx={{ mb: 2 }}
                    slotProps={{
                        transition: {
                            timeout: 150, // Reduced from default 300ms
                        },
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                            flexDirection: 'row-reverse',
                            gap: '8px',
                            '& .MuiAccordionSummary-expandIconWrapper': {
                                marginRight: 0,
                                transform: 'rotate(-90deg)',
                                transition: 'transform 0.2s ease',
                            },
                            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                                transform: 'rotate(0deg)',
                            }
                        }}
                    >
                        <Typography variant="h6">
                            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 1 }}>
                                <HighlightText text={builder.name} searchTerm={searchTerm} />
                                {builder.archived && <Inventory2Icon color='warning' fontSize="small" />}
                            </Box>
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                        {builder.projects.map((project) => (
                            <Accordion
                                key={project.id}
                                expanded={expandedProjects[project.id] || searchTerm || false}
                                onChange={handleExpandProject(project.id)}
                                sx={{
                                    boxShadow: 'none',
                                    '&:before': { display: 'none' }, // Remove divider line
                                    transition: 'margin 150ms ease'
                                }}
                                slotProps={{
                                    transition: {
                                        timeout: 150,
                                    },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        pl: 4,
                                        flexDirection: 'row-reverse',
                                        '& .MuiAccordionSummary-expandIconWrapper': {
                                            transform: 'rotate(-90deg)',
                                        },
                                        '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                                            transform: 'rotate(0deg)',
                                        }
                                    }}
                                >
                                    <Typography variant="h6">
                                        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 1 }}>
                                            <HighlightText variant="subtitle1" text={project.name} searchTerm={searchTerm} />
                                            {project.archived && <Inventory2Icon color='warning' fontSize="small" />}
                                        </Box>
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 1 }}>
                                    <TableContainer component={Paper}
                                        sx={{
                                            boxShadow: 'none', // Removes Paper shadow
                                            border: 'none' // Removes any border
                                        }}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Lot</TableCell>
                                                    <TableCell>Plan #</TableCell>
                                                    <TableCell>Elevation</TableCell>
                                                    <TableCell>Layout</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>Options</TableCell>
                                                    <TableCell>Build Stage</TableCell>
                                                    <TableCell>File Date</TableCell>
                                                    <TableCell>File Name</TableCell>
                                                    <TableCell></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {project.plans.map((plan) => (

                                                    <TableRow key={plan.id} hover>
                                                        <TableCell sx={{ color: "grey" }}>
                                                            <HighlightText text={plan.lot} searchTerm={searchTerm} />
                                                        </TableCell>
                                                        <TableCell sx={{ color: "grey" }}>
                                                            <HighlightText text={plan.planNumber} searchTerm={searchTerm} />
                                                        </TableCell>
                                                        <TableCell sx={{ color: "grey" }}>
                                                            <HighlightText text={plan.elevation} searchTerm={searchTerm} />
                                                        </TableCell>
                                                        <TableCell sx={{ color: "grey" }}>{plan.layout}</TableCell>
                                                        <TableCell sx={{ textAlign: 'center', maxWidth: "250px", color: "grey" }}>
                                                            {plan.options.length === 0 ? null : searchTerm || expandedRows.has(plan.id) ? (
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                                    {plan.options.map((option, index) => (
                                                                        <Tooltip key={index} title={option.planOptionName} arrow>
                                                                            <Link
                                                                                sx={{ cursor: "pointer" }}
                                                                                onClick={() => {
                                                                                    const newSet = new Set(expandedRows);
                                                                                    newSet.delete(plan.id);
                                                                                    setExpandedRows(newSet);
                                                                                }}
                                                                            >
                                                                                <Chip
                                                                                    label={
                                                                                        <Box sx={{
                                                                                            maxWidth: '120px',
                                                                                            whiteSpace: 'nowrap',
                                                                                            overflow: 'hidden',
                                                                                            textOverflow: 'ellipsis',
                                                                                            display: 'inline-block'
                                                                                        }}>
                                                                                            <HighlightText
                                                                                                text={option.planOptionName}
                                                                                                searchTerm={searchTerm}
                                                                                            />
                                                                                        </Box>
                                                                                    }
                                                                                    sx={{
                                                                                        margin: '2px',
                                                                                        '& .MuiChip-label': {
                                                                                            display: 'block',
                                                                                            overflow: 'hidden'
                                                                                        }
                                                                                    }}
                                                                                    size="small"
                                                                                />
                                                                            </Link>
                                                                        </Tooltip>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <Link
                                                                    sx={{ cursor: "pointer" }}
                                                                    onClick={() => {
                                                                        const newSet = new Set(expandedRows);
                                                                        newSet.add(plan.id);
                                                                        setExpandedRows(newSet);
                                                                    }}
                                                                >
                                                                    <Chip label={plan.options.length} size="small" />
                                                                </Link>
                                                            )}
                                                        </TableCell>
                                                        <TableCell sx={{ color: "grey" }}>
                                                            <HighlightText text={plan.buildStages[0]?.buildStageName} searchTerm={searchTerm} />
                                                        </TableCell>
                                                        <TableCell sx={{ color: "grey" }}>
                                                            <Tooltip
                                                                title={
                                                                    `File last modified on ${formatDateTooltip(plan.lastModifiedCadFileDateTime)}. Uploaded on ${formatDateTooltip(plan.uploadedCadFileDateTime)} by ${plan.uploadedCadFileBy || 'unknown'}`
                                                                }
                                                                arrow
                                                            >
                                                                <span>{formatDateTable(plan.lastModifiedCadFileDateTime)}</span>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell sx={{ color: "grey" }}>
                                                            <FileStatusIndicator
                                                                fileName={
                                                                    <HighlightText text={plan.fileName} searchTerm={searchTerm} />
                                                                }
                                                                status={"archived"}
                                                                searchTerm={searchTerm}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <div>
                                                                <IconButton
                                                                    aria-label="more"
                                                                    onClick={(e) => handleMenuClick(e, plan.id)}
                                                                >
                                                                    <MoreVertIcon />
                                                                </IconButton>

                                                                <Menu
                                                                    anchorEl={anchorEl}
                                                                    open={Boolean(anchorEl) && activeMenuRow === plan.id}
                                                                    onClose={handleMenuClose}
                                                                    slotProps={{
                                                                        paper: {
                                                                            sx: {
                                                                                maxHeight: ITEM_HEIGHT * 4.5,
                                                                                width: '20ch',
                                                                            },
                                                                        },
                                                                    }}
                                                                >
                                                                    <MenuItem onClick={() => {
                                                                        const selectedPlan = project.plans.find(p => p.id === activeMenuRow);
                                                                        selectedPlan.builderName = builder.name
                                                                        setViewingPlan(selectedPlan);
                                                                        handleMenuClose();
                                                                    }}>
                                                                        View plan info
                                                                    </MenuItem>

                                                                    {(!builder.archived && !project.archived) && 
                                                                        <WithPermission rolesRequired={['admin', 'superadmin', 'useredit']}>
                                                                            <MenuItem 
                                                                                onClick={() => {
                                                                                    handleRestorePlan(activeMenuRow)
                                                                                    handleMenuClose(); 
                                                                                }}>
                                                                                Restore plan
                                                                            </MenuItem>
                                                                        </WithPermission>
                                                                    }
                                                                </Menu>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </AccordionDetails>
                </Accordion>
            ))}

            {viewingPlan && (
                <ViewArchivedPlanInfo
                    plan={viewingPlan}
                    onClose={() => setViewingPlan(null)}
                    handleRestorePlan={handleRestorePlan}
                />
            )}
        </Box>
    );
};